import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Alert,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";

import DeskWiseOccupancyPage from "./DeskWiseOccupancyPage";
import WorkstationOccupancyChart from "./workstationOccupancyChart";
import WorkstationKpiCards from "./workstationKpiCards";
import WorkspaceOccupancyPie from "./workspaceOccupancyPie";
import MostOccupiedWorkstations from "./MostOccupiedWorkstations";
import {
  getROIConfig,
  getWorkstationCameras,
  getWorkstationOccupancySummary,
} from "../../utils/api";

const WORKSTATION_TYPE = "WORKSTATION";
const REFRESH_INTERVAL = 30 * 1000;

const normalizeCameras = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.cameras)) return response.cameras;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.data)) return response.data;

  return [];
};

const getCameraId = (camera) =>
  String(camera?.camera_id ?? camera?.id ?? "").trim();

const getWorkspaceName = (camera, index) =>
  camera?.label ||
  camera?.name ||
  camera?.camera_name ||
  `Workspace ${index + 1}`;

const getLaneRois = (response) => {
  const rois = response?.lane_rois ?? response?.data?.lane_rois;

  return rois &&
    typeof rois === "object" &&
    !Array.isArray(rois)
    ? rois
    : {};
};

const getSummaryItems = (response) => {
  if (Array.isArray(response?.items)) {
    return response.items;
  }

  if (Array.isArray(response?.data?.items)) {
    return response.data.items;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return [];
};

const buildWorkstations = (laneRois, summaryItems) => {
  const summaryById = new Map(
    summaryItems.map((item) => [
      String(item?.workstation_id ?? item?.id ?? "").trim(),
      item,
    ])
  );

  return Object.keys(laneRois)
    .filter(Boolean)
    .map((id) => {
      const summary = summaryById.get(String(id).trim()) || {};

      return {
        id: String(id),
        status:
          String(summary?.status ?? "").toLowerCase() === "occupied"
            ? "occupied"
            : "idle",
        rect: laneRois[id] || null,
        totalDurationSec: summary?.total_duration_sec ?? 0,
        lastActivityAt: summary?.last_activity_at || null,
      };
    });
};

export default function WorkstationDashboard() {
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboardData = useCallback(async () => {
    setError("");

    try {
      const cameraResponse = await getWorkstationCameras();
      const cameras = normalizeCameras(cameraResponse);

      const results = await Promise.all(
        cameras.map(async (camera, index) => {
          const cameraId = getCameraId(camera);

          if (!cameraId) return null;

          const [roiResult, summaryResult] = await Promise.allSettled([
            getROIConfig(WORKSTATION_TYPE, { cameraId }),
            getWorkstationOccupancySummary({ cameraId }),
          ]);

          const roiResponse =
            roiResult.status === "fulfilled" ? roiResult.value : null;

          const summaryResponse =
            summaryResult.status === "fulfilled"
              ? summaryResult.value
              : null;

          return {
            id: cameraId,
            name: getWorkspaceName(camera, index),
            color:"#2563EB",
            camera,
            workstations: buildWorkstations(
              getLaneRois(roiResponse),
              getSummaryItems(summaryResponse)
            ),
          };
        })
      );

      const validWorkspaces = results.filter(Boolean);

      setWorkspaces(validWorkspaces);

      setSelectedWorkspaceId((previous) => {
        const stillExists = validWorkspaces.some(
          (workspace) => workspace.id === previous
        );

        return previous && stillExists
          ? previous
          : validWorkspaces[0]?.id || "";
      });
    } catch (err) {
      console.error("Failed to load workstation dashboard:", err);

      setError(
        err?.message || "Failed to load workstation dashboard."
      );

      setWorkspaces([]);
      setSelectedWorkspaceId("");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();

    const intervalId = setInterval(
      loadDashboardData,
      REFRESH_INTERVAL
    );

    return () => clearInterval(intervalId);
  }, [loadDashboardData]);

  const overallStats = useMemo(() => {
    const total = workspaces.reduce(
      (sum, workspace) => sum + workspace.workstations.length,
      0
    );

    const occupied = workspaces.reduce(
      (sum, workspace) =>
        sum +
        workspace.workstations.filter(
          (item) => item.status === "occupied"
        ).length,
      0
    );

    return {
      total,
      occupied,
      empty: Math.max(0, total - occupied),
    };
  }, [workspaces]);

  const selectedWorkspace = useMemo(
    () =>
      workspaces.find(
        (workspace) => workspace.id === selectedWorkspaceId
      ) || null,
    [workspaces, selectedWorkspaceId]
  );

  if (loading && workspaces.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />

        <Typography color="text.secondary">
          Loading workstation dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        p: { xs: 1.5, sm: 2, md: 3 },
        boxSizing: "border-box",
      }}
    >
      {error && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Dashboard heading */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#1E293B",
            }}
          >
            Workstation Dashboard
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#64748B",
              mt: 0.5,
            }}
          >
            Monitor workstation occupancy across all workspaces
          </Typography>
        </Box>
      </Box>

      {/* KPI cards */}
      <WorkstationKpiCards
        totalWorkspaces={workspaces.length}
        totalWorkstations={overallStats.total}
        occupiedWorkstations={overallStats.occupied}
        emptyWorkstations={overallStats.empty}
      />

     
      {/* Main dashboard layout */}
<Box
  sx={{
    mt: 2,
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "grid",
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      md: "repeat(2, minmax(0, 1fr))",
    },
    gap: 2,
    alignItems: "stretch",
  }}
>
  {/* Line chart */}
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      "& > *": {
        width: "100%",
        minWidth: 0,
      },
    }}
  >
    <WorkstationOccupancyChart />
  </Box>

  {/* Workspace pie chart */}
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      "& > *": {
        width: "100%",
        minWidth: 0,
      },
    }}
  >
    <WorkspaceOccupancyPie workspaces={workspaces} />
  </Box>

  {/* Bottom-left: Desk-wise occupancy */}
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      gridColumn: { xs: "1 / -1", md: "1 / 2" },
    }}
  >
<DeskWiseOccupancyPage workspaces={workspaces} />  </Box>

  {/* Bottom-right: Most occupied workstations */}
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      gridColumn: { xs: "1 / -1", md: "2 / 3" },
    }}
  >
    <MostOccupiedWorkstations workspaces={workspaces} />
  </Box>
</Box>
    </Box>
  );
}